import React, { useCallback, useState, useMemo, useEffect } from 'react'
import Modal from 'react-modal'
import classNames from 'classnames'
import { useSelector, useDispatch } from 'react-redux'
import { useForm, Controller } from 'react-hook-form'

import Tags from 'Src/components/TagsInput'

import api from 'Src/api'

import { getBotById } from 'Selectors/bots'
import { fetchBots } from 'Src/actions/bots'

const DEPLOY_BOT_URL = 'v1/bot-profiles/deploy/:id'

const DeployBotModal = ({ isOpen, hide, botId }) => {
  const [apiError, setApiError] = useState(null)
  const [deploymentSuccess, setDeploymentSuccess] = useState(false)

  const dispatch = useDispatch()

  const bot = useSelector((state) => getBotById(state, botId))
  const {
    handleSubmit,
    register,
    formState: { errors },
    control,
    watch
  } = useForm({
    defaultValues: {
      botAgentEmail: '',
      profileId: bot.settings?.profile?.[0],
      mlModelId: bot.settings?.ml?.modelId ?? 0,
      transferDepartmentId: bot.settings?.transferDepartmentId,
      openingQuickReplies: []
    }
  })

  const onClose = useCallback(hide, [hide])

  const deployBot = async (data) => {
    const { botAgentEmail, profileId, mlModelId, transferDepartmentId, openingQuickReplies } = data

    const botPayload = {
      botAgentEmail,
      profileId,
      transferDepartmentId,
      quickReplies: openingQuickReplies,
      mlModelId: isNaN(mlModelId) ? 0 : mlModelId
    }

    try {
      await api.patch(DEPLOY_BOT_URL, botPayload, { urlParams: { id: botId } })
      setDeploymentSuccess(true)
      setApiError(null)
      dispatch(fetchBots())
    } catch (error) {
      console.error('Error while bottifying an agent', error)
      setDeploymentSuccess(false)
      setApiError(error.response.data)
    }
  }

  const botAgentEmail = useMemo(
    () =>
      register('botAgentEmail', {
        required: 'Agent Email is required',
        pattern: {
          value: /^[A-Z0-9._-]+\+[A-Z0-9.-]*bot[A-Z0-9.-]*@[A-Z0-9.-]+\.[A-Z]{2,4}$/i,
          message: "Please include '+bot' in the email address. Eg. 'joe+bot@email.com'"
        }
      }),
    [register]
  )

  const profileId = useMemo(() => register('profileId', { required: 'Widget Id is required' }), [register])
  const mlModelId = useMemo(() => register('mlModelId', { valueAsNumber: true }), [register])
  const transferDepartmentId = useMemo(() => register('transferDepartmentId'), [register])

  const watchAllFields = watch()

  useEffect(() => {
    setApiError(null)
  }, [
    watchAllFields.botAgentEmail,
    watchAllFields.profileId,
    watchAllFields.transferDepartmentId,
    watchAllFields.openingQuickReplies
  ])

  return (
    <Modal
      isOpen={isOpen}
      contentLabel='Deploy Bot Modal'
      ariaHideApp={false}
      onRequestClose={onClose}
      style={{
        content: {
          width: '500px'
        }
      }}
    >
      <h2>Deploy {bot.name}</h2>
      <form onSubmit={handleSubmit(deployBot)}>
        <div className='form-group'>
          <label>Bot Agent Email</label>
          <input
            className={classNames('form-control', { 'is-invalid': errors.name })}
            name='botAgentEmail'
            type='email'
            {...botAgentEmail}
          />
          {errors.botAgentEmail && <small className='form-text text-danger'>{errors.botAgentEmail.message}</small>}
        </div>

        <div className='form-group'>
          <label>Widget ID</label>
          <input
            className={classNames('form-control', { 'is-invalid': errors.name })}
            name='profileId'
            {...profileId}
          />
          {errors.profileId && <small className='form-text text-danger'>{errors.profileId.message}</small>}
        </div>

        <div className='form-group'>
          <label>Opening quick replies</label>
          <Controller
            name='openingQuickReplies'
            control={control}
            rules={{
              validate: (value) => {
                if (!value.length) {
                  return 'Please add atleast one opening quick reply'
                }
              }
            }}
            render={({ field }) => {
              return <Tags tags={field.value} placeholder='Add a quick reply' {...field} />
            }}
          />
          {errors.openingQuickReplies && (
            <small className='form-text text-danger'>{errors.openingQuickReplies.message}</small>
          )}
        </div>

        <div className='form-group'>
          <label>ML Model ID (Optional)</label>
          <input type='number' className='form-control' name='mlModelId' {...mlModelId} />
        </div>

        <div className='form-group'>
          <label>Transfer Department ID (Optional)</label>
          <input className='form-control' name='transferDepartmentId' {...transferDepartmentId} />
        </div>

        <button className='btn btn-sm btn-primary' type='submit'>
          Bottify
        </button>

        {deploymentSuccess && (
          <div className='alert alert-success mt-2' role='alert'>
            Deployed!
          </div>
        )}
        {apiError && (
          <div className='alert alert-danger mt-2' role='alert'>
            {apiError.message && <span>{apiError.message}</span>}
          </div>
        )}
      </form>
    </Modal>
  )
}

export default DeployBotModal
